import { getExperiment } from '@/utils/experiment/use-experiment';
import { wacRefreshJsm } from '@/utils/experiment/wac-refresh-experiments';

export const JSM_PLAYGROUND_SIGNUP_SOURCE = 'playground';
export const JSM_PLAYGROUND_SIGNUP_INTENT_CHANGE_EVENT = 'jsm-playground-signup-intent-change';

export const JSM_PLAYGROUND_TEAM_TYPES = [
	'it-support',
	'operations',
	'software-development',
	'human-resources',
] as const;

export type JsmPlaygroundTeamType = (typeof JSM_PLAYGROUND_TEAM_TYPES)[number];

export type JsmPlaygroundSignupIntent = {
	signupSource: typeof JSM_PLAYGROUND_SIGNUP_SOURCE;
	teamType: JsmPlaygroundTeamType;
};

export const isJsmPlaygroundSignupIntentEnabled = (): boolean => {
	const experiment = getExperiment(wacRefreshJsm);

	return experiment.type === 'enrolled' && experiment.value;
};

export const isJsmPlaygroundSignup = (
	search: string | URLSearchParams | undefined,
	isEnabled?: boolean,
): boolean => {
	if (!search) {
		return false;
	}

	const searchParameters = typeof search === 'string' ? new URLSearchParams(search) : search;
	if (searchParameters.get('signupSource') !== JSM_PLAYGROUND_SIGNUP_SOURCE) {
		return false;
	}

	return isEnabled ?? isJsmPlaygroundSignupIntentEnabled();
};

export const getJsmPlaygroundSignupIntent = (
	search: string | URLSearchParams | undefined,
	isEnabled?: boolean,
): JsmPlaygroundSignupIntent | undefined => {
	if (!search) {
		return undefined;
	}

	const searchParameters = typeof search === 'string' ? new URLSearchParams(search) : search;
	const signupSource = searchParameters.get('signupSource');
	const teamType = searchParameters.get('team_type');

	if (
		signupSource !== JSM_PLAYGROUND_SIGNUP_SOURCE ||
		!JSM_PLAYGROUND_TEAM_TYPES.includes(teamType as JsmPlaygroundTeamType)
	) {
		return undefined;
	}

	if (!(isEnabled ?? isJsmPlaygroundSignupIntentEnabled())) {
		return undefined;
	}

	return {
		signupSource,
		teamType: teamType as JsmPlaygroundTeamType,
	};
};

export const setJsmPlaygroundSignupIntent = (
	searchParameters: URLSearchParams,
	teamType: JsmPlaygroundTeamType,
) => {
	searchParameters.set('signupSource', JSM_PLAYGROUND_SIGNUP_SOURCE);
	searchParameters.set('team_type', teamType);
	searchParameters.delete('templateId');
};

const isJsmSignupPath = (pathname: string) =>
	pathname.endsWith('/try/cloud/signup') ||
	pathname.endsWith('/software/jira/service-management/free');

export const addJsmPlaygroundSignupIntentToHref = (
	href: string,
	currentSearch: string,
	origin = 'https://www.atlassian.com',
	isEnabled?: boolean,
): string => {
	const intent = getJsmPlaygroundSignupIntent(currentSearch, isEnabled);
	if (!intent) {
		return href;
	}

	const targetUrl = new URL(href, origin);
	if (!isJsmSignupPath(targetUrl.pathname)) {
		return href;
	}

	setJsmPlaygroundSignupIntent(targetUrl.searchParams, intent.teamType);

	return href.startsWith('/')
		? `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
		: targetUrl.toString();
};
